import ms from "ms";
import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { ITuvisMessagingProvider } from "@whatslly/ui-components/Pages/TuvisMessaging/TuvisMessagingProvider";
import { TuvisMessagingProviderSymbol } from "@whatslly/ui-components/Pages/TuvisMessaging/TuvisMessagingProvider";
import { getHost } from "@whatslly/commons/apiUtils";
import { DataCollection } from "./DataCollection";
import { IMChat } from "./IMChat";
import { IMConversation } from "./IMConversation";
import { IMMessage } from "./IMMessage";
import type { StringifiedMutation, StreamMutation, MessagingRepo } from "@whatslly/types/tuvis-messaging/TuvisMessagingStream";
import { IMContact } from "./IMContact";
import { MessagingClient } from "@whatslly/commons/messagingClient";
import type { MessagingCommandsTypes } from "@whatslly/commons/tuvis-messaging/messagingCommands";
import { MessagingCommands } from "@whatslly/commons/tuvis-messaging/messagingCommands";
import { ImMessage } from "@whatslly/types/tuvis-messaging/TuvisMessagingMessages";
import type { ImConversation } from "@whatslly/types/tuvis-messaging/TuvisMessagingConversations";
import { IMLabel } from "@whatslly/ui-components/Pages/TuvisMessaging/services/IMLabel";
import type { IDataStore } from "@whatslly/ui-components/Pages/TuvisMessaging/services/IDataStore";
import { IMCampaign } from "./IMCampaign";
import { IMCampaignItem } from "./IMCampaignItem";

const WS_RECCONECT_INTERVAL = ms("3s");
const WS_KEEP_ALIVE_INTERVAL = ms("30s"); // alb has a 60s timeout. need to keep the connection alive otherwise it will be closed
const MAX_WS_RECONNECT_ATTEMPTS = 30000;

@injectable()
export class TuvisMessagingDataStore implements IDataStore {
  intialized: Promise<void> | undefined;
  private initialConnection: { promise: Promise<void>; resolve: () => void } = { promise: Promise.resolve(), resolve: () => {} };
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private lastMsgId: string | null = null;
  private keepAliveInterval: number | undefined;
  public wsClient = new MessagingClient<MessagingCommandsTypes>(MessagingCommands);
  public chats = new DataCollection(IMChat, "_id");
  public messages = new DataCollection(IMMessage, "_id");
  public contacts = new DataCollection(IMContact, "_id");
  public labels = new DataCollection(IMLabel, "_id");
  public campaigns = new DataCollection(IMCampaign, "_id");
  public campaignItems = new DataCollection(IMCampaignItem, "_id");
  public conversations = new DataCollection(IMConversation, "_id");
  constructor(
    @inject(FrontLogger) private readonly logger: FrontLogger,
    @inject(TuvisMessagingProviderSymbol) private readonly dataProvider: ITuvisMessagingProvider
  ) {
    IMChat.dataStore = this;
    this.initialConnection.promise = new Promise((resolve) => (this.initialConnection.resolve = resolve));
    this.wsClient.init((msg) => this.socket?.send(JSON.stringify(msg)));
  }

  onOpen = async () => {
    this.logger.info("websocket connected");
    const { chats, contacts, messages, labels, delta, conversations } = await this.wsClient.send(MessagingCommands.GET_INIT_DATA, {
      lastMsgId: this.lastMsgId,
    });
    contacts.forEach((contact) => this.contacts.insert(contact));
    chats.forEach((chat) => this.chats.insert(chat));
    messages.forEach((message) => this.insertMessage(message));
    delta.forEach((msg) => this.onMessage({ data: JSON.stringify(msg) } as any));
    labels.forEach((label) => this.labels.insert(label));
    conversations?.forEach((conversation) => this.conversations.insert(conversation));
    this.initialConnection.resolve();
    this.keepAlive();
  };
  onMessage = (event: { data: any }) => {
    const _msg = JSON.parse(event.data) as
      | (StringifiedMutation<MessagingRepo> & { msgId: string })
      | Parameters<TuvisMessagingDataStore["wsClient"]["respond"]>[0];
    if ("cmd" in _msg || "transactionId" in _msg) {
      return this.wsClient.respond(_msg as any);
    }

    this.lastMsgId = _msg.msgId;
    const message = Object.assign(_msg, {
      data: _msg.data$ ?? JSON.parse(_msg.data),
      key: _msg.key$ ?? JSON.parse(_msg.key),
      options: _msg.options && JSON.parse(_msg.options),
    }) as StreamMutation<MessagingRepo>;

    if (message.action === "create") {
      if (message.repository === "messages") {
        this.insertMessage(message.data as ImMessage);
      } else {
        this[message.repository].insert(message.data as any);
      }
    } else if (message.action === "update") {
      this[message.repository].update(message.key, message.data as any, message.options);
    }
  };
  onError = (err: any) => {
    if (err.code === "ECONNREFUSED") {
      return this.reconnect();
    }
    this.logger.error({ err }, "error in websocket connection");
  };
  onClose = (event?: { code?: number | undefined }) => {
    if (event?.code === 1000) {
      this.logger.info("websocket closed normally");
      return;
    }
    this.reconnect();
  };

  private async connect() {
    clearInterval(this.keepAliveInterval);
    if (this.reconnectAttempts++ > MAX_WS_RECONNECT_ATTEMPTS) {
      this.logger.error("websocket connection failed, max reconnect attempts reached");
      return;
    }
    const orgId = await this.dataProvider.getOrgId();
    try {
      const data = await this.dataProvider.getMessages?.();
      data?.forEach((message: ImMessage) => {
        this.insertMessage(message);
      });
    } catch (e) {
      // nope
    }

    const url = new URL(import.meta.env.VITE_MESSAGING_WS_URL || `${getHost("/messaging-ws", this.dataProvider.pod)}/messaging-ws`);
    url.searchParams.append("etoken", this.dataProvider.etoken);
    if (orgId) url.searchParams.append("orgId", orgId);
    this.socket = new WebSocket(url.toString().replace(/^https?/, import.meta.env.VITE_API_MAP_TO_PORT_BY_PATH ? "ws" : "wss"));
    this.socket.addEventListener("open", this.onOpen);
    this.socket.addEventListener("message", this.onMessage);
    this.socket.addEventListener("error", this.onError);
    this.socket.addEventListener("close", this.onClose);
    try {
      this.dataProvider.newMessagesEmitter?.((message: ImMessage) => {
        this.insertMessage(message);
      });
    } catch (e) {
      // nope
    }
  }

  private clearSocket() {
    this.socket?.removeEventListener("open", this.onOpen);
    this.socket?.removeEventListener("message", this.onMessage);
    this.socket?.removeEventListener("error", this.onError);
    this.socket?.removeEventListener("close", this.onClose);
    this.socket = null;
  }

  private reconnect() {
    this.logger.warn({ reconnectAttempts: this.reconnectAttempts }, "websocket connection closed, reconnecting");
    this.clearSocket();
    setTimeout(() => this.connect(), WS_RECCONECT_INTERVAL);
  }

  private insertMessage(message: ImMessage) {
    if (message.messageId && this.messages.find((m) => m.messageId === message.messageId)) {
      return;
    }
    this.messages.insert(message);
  }

  private async keepAlive() {
    try {
      await this.wsClient.send(MessagingCommands.PING);
    } catch (err) {
      this.logger.error({ err }, "failed to send ping");
    }
    this.keepAliveInterval = setTimeout(() => this.keepAlive(), WS_KEEP_ALIVE_INTERVAL) as unknown as number;
  }

  public disconnect() {
    if (this.socket) {
      this.reconnectAttempts = 0;
      clearInterval(this.keepAliveInterval);
      this.socket.close(1000);
      this.clearSocket();
      this.chats.items.clear();
      this.messages.items.clear();
      this.contacts.items.clear();
      this.labels.items.clear();
      this.conversations.items.clear();
      this.intialized = undefined;
      this.initialConnection.promise = new Promise((resolve) => (this.initialConnection.resolve = resolve));
      this.lastMsgId = null;
    }
  }

  async _init() {
    await this.connect();
    await this.initialConnection.promise;
  }

  async init() {
    if (!this.dataProvider.etoken || this.intialized) {
      return this.intialized;
    }
    this.intialized = this._init();
    return this.intialized;
  }
}
